import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Award, ArrowRight, CheckCircle2 } from 'lucide-react';

const API = `${window.location.origin}/api`;

/**
 * /r/:slug — referral landing page.
 * Hits the backend /api/r/:slug to set the cookie, then auto-redirects to home.
 */
export default function ReferralLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [ambassador, setAmbassador] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Existing cookie-setting endpoint (track the ambassador for the
    // next 30 days)…
    axios.get(`${API}/r/${slug}`, { withCredentials: true })
      .then(res => {
        setAmbassador(res.data.ambassador);
        const t = setTimeout(() => navigate('/shop'), 3500);
        return () => clearTimeout(t);
      })
      .catch(() => setError('This referral link is invalid or has expired.'));

    // …plus, in parallel, log the visit with UTM context. This populates
    // the admin "Referral Analytics" dashboard so each ambassador can see
    // which channel (FB vs TikTok vs IG vs DM) drives their traffic.
    try {
      const qs = new URLSearchParams(window.location.search);
      axios.post(`${API}/referrals/track`, {
        slug,
        source: qs.get('utm_source') || qs.get('src') || '',
        medium: qs.get('utm_medium') || '',
        campaign: qs.get('utm_campaign') || '',
        path: window.location.pathname + window.location.search,
      }).catch(() => { /* analytics is best-effort, never block UX */ });
    } catch { /* malformed URL */ }
  }, [slug, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <p className="text-white/60 text-sm font-sans mb-4">{error}</p>
          <Link to="/" className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] underline hover:text-white">Continue to JS Fragrance</Link>
        </div>
      </div>
    );
  }

  if (!ambassador) {
    return <div className="min-h-screen flex items-center justify-center text-white/40 text-sm">Setting up your referral…</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="referral-landing">
      <div className="max-w-lg text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
          <Award className="w-3.5 h-3.5 text-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-sans">{ambassador.tier?.name || 'Ambassador'}</span>
        </div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3 font-sans">You've been recommended</p>
        <h1 className="text-4xl sm:text-5xl tracking-tight leading-tight font-light text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Welcome from <span className="text-[#D4AF37]">{ambassador.display_name}</span>
        </h1>
        <p className="text-white/50 text-sm font-sans leading-relaxed mb-8">
          A curated selection of premium fragrances awaits you. Your visit has been registered — any purchase
          you make in the next 30 days supports {ambassador.display_name.split(' ')[0]}.
        </p>
        <div className="flex items-center justify-center gap-2 text-[#1C7D3F] text-xs uppercase tracking-[0.2em] font-sans mb-8">
          <CheckCircle2 className="w-3.5 h-3.5" /> Referral activated
        </div>
        <button
          onClick={() => navigate('/shop')}
          data-testid="referral-cta"
          className="inline-flex items-center gap-2 px-8 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.25em] font-sans font-medium hover:bg-[#E5C75A] transition-colors"
        >
          Explore Collection <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <p className="text-white/20 text-[10px] font-sans mt-6">Auto-redirecting in a moment…</p>
      </div>
    </div>
  );
}
